import { OptionType, TFilterOptions } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { CUSTOM_FIELDS_FILTER_PARAM_KEY } from 'src/apps/dashboard/constants/CustomFields';
import {
	ANDROID_OS_VALUES,
	IOS_OS_VALUES,
	MOBILE_APP_ID_FILTER_ID,
	MOBILE_PLATFORM_FILTER_ID,
	MobilePlatform,
	parseMobilePlatform
} from 'src/apps/dashboard/constants/MobileDashboard.constants';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { buildTupleString } from 'src/common/helpers/GQLRequestsHelper/buildTupleString';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';
import {
	ConditionOperators,
	ConditionString,
	ConditionStringArray
} from 'src/common/types/graphql.generated';

interface IMobileRunFilters {
	mfaOsFilter?: ConditionString;
	mfaAppIdFilter?: ConditionStringArray;
	msdkOsFilter?: ConditionString;
	msdkAppFilter?: ConditionString;
}

const MOBILE_FILTER_KEYS = [
	'dateRange',
	'searchValue',
	'severities',
	MOBILE_PLATFORM_FILTER_ID,
	MOBILE_APP_ID_FILTER_ID
];

export const getPlatformFromFilters = (filters?: IFilterOptions): MobilePlatform =>
	parseMobilePlatform(filters?.[MOBILE_PLATFORM_FILTER_ID]?.[0]?.value);

export const hasSelectedBusinessUnit = (unitId?: OptionType[]): boolean =>
	Boolean(unitId && unitId.length > 0 && unitId[0].value !== '');

const buildMfaOsFilter = (platform: MobilePlatform): ConditionString | undefined => {
	if (platform === 'all') {
		return undefined;
	}

	const values = platform === 'ios' ? IOS_OS_VALUES : ANDROID_OS_VALUES;
	return { value: values };
};

const buildMsdkOsFilter = (platform: MobilePlatform): ConditionString | undefined => {
	if (platform === 'all') {
		return undefined;
	}

	const values = platform === 'ios' ? IOS_OS_VALUES : ANDROID_OS_VALUES;
	return { value: values };
};

export const getMobileAppIdsFromFilters = (filters?: IFilterOptions): string[] => {
	const appIdFilter = filters?.[MOBILE_APP_ID_FILTER_ID];

	if (!appIdFilter) {
		return [];
	}

	if (Array.isArray(appIdFilter) && appIdFilter.length > 0) {
		return (appIdFilter as OptionType[]).map((opt) => String(opt.value)).filter(Boolean);
	}

	return [];
};

const buildMfaAppIdFilter = (filters?: IFilterOptions): ConditionStringArray | undefined => {
	const appIds = getMobileAppIdsFromFilters(filters);
	if (!appIds.length) {
		return undefined;
	}

	return {
		value: [appIds],
		operator: ConditionOperators.All
	};
};

const buildMsdkAppFilter = (filters?: IFilterOptions): ConditionString | undefined => {
	const appIds = getMobileAppIdsFromFilters(filters);
	if (!appIds.length) {
		return undefined;
	}

	return { value: appIds };
};

export const buildMobileRunFilters = (filters?: IFilterOptions): IMobileRunFilters => {
	const platform = getPlatformFromFilters(filters);

	return {
		mfaOsFilter: buildMfaOsFilter(platform),
		mfaAppIdFilter: buildMfaAppIdFilter(filters),
		msdkOsFilter: buildMsdkOsFilter(platform),
		msdkAppFilter: buildMsdkAppFilter(filters)
	};
};

const buildMobileCustomFieldsVariables = (
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): {
	customFieldsFilter?: ConditionStringArray;
	customFieldsFilterExcludes?: ConditionStringArray;
} => {
	const variablesBuilder = new QueryVariablesBuilder<Record<string, unknown>>();

	if (filters) {
		const customFieldOptions = omit(filters, MOBILE_FILTER_KEYS) as TFilterOptions;

		variablesBuilder.addTupleFilterParam(
			CUSTOM_FIELDS_FILTER_PARAM_KEY,
			customFieldOptions,
			ConditionOperators.All,
			true
		);
	}

	if (labelsExcludedFromDashboard && Object.keys(labelsExcludedFromDashboard).length > 0) {
		variablesBuilder.addNeTupleFilterParam(
			'customFieldsFilterExcludes',
			labelsExcludedFromDashboard
		);
	}

	const variables = variablesBuilder.getVariables();

	return {
		customFieldsFilter: variables.customFieldsFilter as ConditionStringArray | undefined,
		customFieldsFilterExcludes: variables.customFieldsFilterExcludes as
			| ConditionStringArray
			| undefined
	};
};

export const buildMobilePlatformConditionFilter = (
	filters?: IFilterOptions
): ConditionString | undefined => {
	const platform = getPlatformFromFilters(filters);
	if (platform === 'all') {
		return undefined;
	}
	return { value: platform === 'ios' ? IOS_OS_VALUES : ANDROID_OS_VALUES };
};

export const buildMobileBuCustomFieldsFilter = (
	mobileBuIds?: string[]
): ConditionStringArray | undefined => {
	if (!mobileBuIds?.length) {
		return undefined;
	}
	return {
		value: [mobileBuIds.map((buId) => buildTupleString('unitId', buId))],
		operator: ConditionOperators.All
	};
};

export const buildMobileBaseVariables = (
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard
): IMobileRunFilters & {
	customFieldsFilter?: ConditionStringArray;
	customFieldsFilterExcludes?: ConditionStringArray;
} => ({
	...buildMobileRunFilters(filters),
	...buildMobileCustomFieldsVariables(filters, labelsExcludedFromDashboard)
});
